/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { RoutesEditor } from "../../RoutesEditor";
import { ToolbarSpacer } from "../../../../../ui/components/Toolbar/ToolbarSpacer";
import { ToolbarButton } from "../../../../../ui/components/Toolbar/ToolbarButton";
import { useDeepCompareMemo } from "use-deep-compare";
import { useResourceInfo, useStateWithEvent, useViewInfo } from "../../../../../ui/hooks";
import { ToolbarCommandButton } from "../../../../../ui/components/Toolbar/ToolbarCommandButton";

export interface IRoutesEditorToolbarProps {
	view: RoutesEditor;
}

export const RoutesEditorToolbar: React.FC<IRoutesEditorToolbarProps> = ({ view }) => {
	const viewInfo = useViewInfo(view);
	const resourceInfo = useResourceInfo(view);
	const saveAllowed = useDeepCompareMemo(() => view.canSave(), [ viewInfo, resourceInfo ]);

	const routesModel = useMemo(() => view.getRoutesModel(), []);

	const undo = useCallback(() => view.undo(), [ view ]);
	const redo = useCallback(() => view.redo(), [ view ]);

	const history = useStateWithEvent(
		routesModel.onHistoryChange,
		() => {
			const undoAllowed = routesModel.undoRedoManager.canUndo();
			const redoAllowed = routesModel.undoRedoManager.canRedo();

			return {
				undoAllowed,
				redoAllowed
			};
		},
		[ routesModel, view ]
	);

	return (
		<>
			<ToolbarButton icon="mdi/undo-variant" disabled={!history.undoAllowed} onClick={undo} />

			<ToolbarButton icon="mdi/redo-variant" disabled={!history.redoAllowed} onClick={redo} />

			<ToolbarSpacer grow={1} />

			<ToolbarCommandButton
				icon="mdi/content-save"
				commandId="view.save"
				disabled={!saveAllowed}
				tooltip="Save"
			/>
		</>
	);
};
